import React, {useState} from 'react';
import {Box, Stack, Collapse, Typography, IconButton} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import {useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import './SortablePanel.scss';
import ArrSelectButton from '../../../../common/arr-select-button/ArrSelectButton';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import ArrTooltipWrapper from '../../../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {Query} from '../../../types/Query';
import {
  getOperators,
  getOperatorByType,
  getOperatorByValue,
} from '../operatorButtonUtils';

export interface SortablePanelProps {
  query: Query;
  title: string;
  tooltip: string;
  children?: React.ReactNode;
  onDelete?: (query: Query) => void;
  onOperatorChanged?: (query: Query) => void;
  dataTestId: string;
}

const SortablePanel: React.FC<SortablePanelProps> = ({
  query,
  title,
  tooltip,
  children,
  onDelete,
  onOperatorChanged,
  dataTestId,
}) => {
  const [expanded, setExpanded] = useState(true);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({id: query.type});

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleDeleteButtonClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onDelete && onDelete(query);
  };

  const handleDeleteButtonKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      onDelete && onDelete(query);
    }
  };

  const handleExpandCollapseButtonClick = (
    e: React.MouseEvent<HTMLElement>
  ) => {
    e.stopPropagation();
    setExpanded((prev) => !prev);
  };

  const handleExpandCollapseButtonKeyDown = (
    e: React.KeyboardEvent<HTMLElement>
  ) => {
    if (e.key === 'Enter' || e.code === 'Enter') {
      e.stopPropagation();
      setExpanded((prev) => !prev);
    }
  };

  const handleSelectionChange = (option: string) => {
    const updatedQuery: Query = {
      type: query.type,
      operator: getOperatorByValue(option),
      value: query.value ? JSON.parse(JSON.stringify(query.value)) : [],
    };
    onOperatorChanged && onOperatorChanged(updatedQuery);
  };

  const renderItem = () => {
    return (
      <Box
        ref={setNodeRef}
        style={style}
        className={`sortable-panel ${isDragging ? 'dragging' : ''}`}
        tabIndex={0}
      >
        {/* Panel Header */}
        <Stack
          direction={'row'}
          spacing={1}
          className={`sortable-panel-header ${expanded ? 'expanded' : 'collapsed'}`}
        >
          <Box
            className="sortable-panel-header_handle"
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            tabIndex={-1}
          >
            <ArrIconButton
              icon={<MenuIcon data-testid={`svg-${dataTestId}`} />}
              ariaLabel=""
              onClick={() => {}}
              size="small"
              className="icon-drag"
              testId={`${dataTestId}-sortable-panel-drag`}
            />
          </Box>
          <Box className="sortable-panel-header_title" tabIndex={0}>
            <Typography
              className="typo-text subhead4_size"
              data-testid={`sortable-panel-title-${dataTestId}`}
            >
              {title}
            </Typography>
          </Box>
          <Box className="sortable-panel-header_tooltip">
            <ArrTooltipWrapper
              trigger={
                <IconButton
                  aria-label="Info"
                  sx={{padding: 0}}
                  className="icon-info"
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    data-testid={`svg-info-${dataTestId}`}
                  />
                </IconButton>
              }
            >
              <Trans
                i18nKey={tooltip}
                components={{
                  anchor: (
                    <a
                      href="https://aurora-review.consilio.com/"
                      target="_blank"
                      data-testid={`sortable-panel-a`}
                    />
                  ),
                  grid: (
                    <table
                      border={1}
                      style={{
                        borderSpacing: '0',
                        wordWrap: 'normal',
                        wordBreak: 'normal',
                      }}
                      data-testid={`sortable-panel-table`}
                    />
                  ),
                  gbody: <tbody data-testid={`sortable-panel-tbody`} />,
                  gr: <tr data-testid={`sortable-panel-tr`} />,
                  gd: <td data-testid={`sortable-panel-td`} />,
                  unorderedList: <ul data-testid={`sortable-panel-ul`} />,
                  listItem: <li data-testid={`sortable-panel-li`} />,
                }}
              />
            </ArrTooltipWrapper>
          </Box>
          <Box className="sortable-panel-header_actions">
            <ArrIconButton
              icon={
                <DeleteOutlineIcon
                  data-testid={`${dataTestId}-sortable-panel-delete-svg`}
                />
              }
              onClick={handleDeleteButtonClick}
              onKeyDown={handleDeleteButtonKeyDown}
              ariaLabel={t('sessionSearch.queryList.delete')}
              tooltipContent={t('sessionSearch.queryList.delete')}
              size="small"
              className="icon-delete"
              testId={`${dataTestId}-sortable-panel-delete`}
            />
            <ArrIconButton
              icon={
                expanded ? (
                  <ExpandLessIcon
                    data-testid={`${dataTestId}-sortable-panel-expand-less`}
                  />
                ) : (
                  <ExpandMoreIcon
                    data-testid={`${dataTestId}-sortable-panel-expand-more`}
                  />
                )
              }
              onClick={handleExpandCollapseButtonClick}
              onKeyDown={handleExpandCollapseButtonKeyDown}
              ariaLabel={
                expanded
                  ? t('sessionSearch.queryList.collapse')
                  : t('sessionSearch.queryList.expand')
              }
              tooltipContent={
                expanded
                  ? t('sessionSearch.queryList.collapse')
                  : t('sessionSearch.queryList.expand')
              }
              size="small"
              className="icon-expand_collapse"
              testId={`${dataTestId}-sortable-panel-expand-collapse`}
            />
          </Box>
        </Stack>
        {/* Panel Content */}
        <Collapse in={expanded}>
          <Box className="sortable-panel-content">{children}</Box>
        </Collapse>
        {/* Panel Operator */}
        <Box className="sortable-panel-operator">
          <ArrSelectButton
            size="xs"
            className="icon-swap"
            dataTestId={`${dataTestId}-sortable-panel-operator`}
            options={getOperators()}
            defaultOption={getOperatorByType(query.operator)}
            onSelectionChange={handleSelectionChange}
          />
        </Box>
      </Box>
    );
  };

  const renderPlaceholder = () => {
    return (
      <hr
        ref={setNodeRef}
        style={style}
        className="sortable-panel-placeholder"
        data-testid={`sortable-panel-hr`}
      />
    );
  };

  return <>{isDragging ? renderPlaceholder() : renderItem()}</>;
};

export default SortablePanel;
